import { useEffect, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { LIST_PALETTE, getListPalette } from "../../constants/listPalette";
import InlineAssigneePicker from "./InlineAssigneePicker";
import { CoAssigneeAvatars } from "./AssigneeAvatars";
import InlinePriorityPicker from "./InlinePriorityPicker";
import { canEditProtectedTaskFields } from "../../lib/taskPermissions";
import { Pencil, Palette, Smile, X } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const EmojiPicker = lazy(() => import("emoji-picker-react"));

const STATUSES = ["todo", "in_progress", "done"];

/**
 * Kanban board — pipeline columns for a project.
 *
 * As of iter47 each column can be renamed / recolored / emoji-ified per
 * tenant via a right-click context menu (backend: `/api/kanban/columns`).
 * The underlying `task.status` enum is untouched — this is a purely visual
 * overlay so pipelines can be branded ("Backlog" ➜ "İnceleme" ➜ "Hazır")
 * without touching workflow logic elsewhere.
 */
export default function KanbanView({ lists, tasks, users, currentUser, onOpen, onUpdate }) {
  const { t } = useTranslation();
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const [prefs, setPrefs] = useState({});
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/kanban/columns");
        if (!alive) return;
        const map = {};
        (data || []).forEach((c) => {
          map[c.status] = c;
        });
        setPrefs(map);
      } catch {
        // Non-fatal: falls back to i18n defaults with no color/emoji.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const patchColumn = async (status, patch, successMsg) => {
    try {
      const { data } = await api.patch(`/kanban/columns/${status}`, patch);
      setPrefs((prev) => ({ ...prev, [status]: data }));
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Kolon güncellenemedi",
      );
    }
  };

  const openRename = (status) => {
    setRenameTarget(status);
    setRenameValue(prefs[status]?.label || "");
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    setSavingRename(true);
    try {
      const nextLabel = renameValue.trim();
      const { data } = await api.patch(`/kanban/columns/${renameTarget}`, {
        label: nextLabel,
      });
      setPrefs((prev) => ({ ...prev, [renameTarget]: data }));
      toast.success(
        nextLabel
          ? `Kolon "${nextLabel}" olarak adlandırıldı`
          : "Kolon adı sıfırlandı",
      );
      setRenameTarget(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Kolon güncellenemedi",
      );
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STATUSES.map((status) => {
          const pref = prefs[status] || {};
          const palette = getListPalette(pref.color);
          const label = (pref.label && pref.label.trim())
            ? pref.label
            : t(`tasks.status_${status}`);
          return (
            <div
              key={status}
              className="flex w-[320px] shrink-0 flex-col gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const id = e.dataTransfer.getData("task-id");
                if (!id) return;
                const dropped = tasks.find((t) => t.id === id);
                // Same row-level RBAC as PATCH /tasks/{id}. Non-owners
                // get a friendly toast instead of a silent no-op so
                // they know WHY the card snapped back.
                if (!canEditProtectedTaskFields(currentUser, dropped)) {
                  toast.error(
                    "Bu görev sana atanmamış — yalnızca kendi görevlerini taşıyabilirsin",
                  );
                  return;
                }
                onUpdate(id, { status });
              }}
            >
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div
                    data-testid={`kanban-column-header-${status}`}
                    title="Sağ tık: yeniden adlandır, renk veya emoji seç"
                    className={[
                      "flex items-center justify-between gap-2 rounded-md px-2 py-1 cursor-context-menu transition-colors",
                      pref.color ? palette.chip : "hover:bg-zinc-100",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold">
                      {pref.emoji ? (
                        <span
                          className="text-sm leading-none"
                          data-testid={`kanban-column-emoji-${status}`}
                          aria-hidden
                        >
                          {pref.emoji}
                        </span>
                      ) : null}
                      <span
                        className={pref.color ? "" : "text-zinc-500"}
                        data-testid={`kanban-column-label-${status}`}
                      >
                        {label}
                      </span>
                    </div>
                    <span className={pref.color ? "text-xs font-medium" : "text-xs text-zinc-500"}>
                      {tasks.filter((tk) => tk.status === status).length}
                    </span>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent
                  className="w-52"
                  data-testid={`kanban-column-menu-${status}`}
                >
                  <ContextMenuItem
                    onSelect={() => openRename(status)}
                    data-testid={`kanban-column-rename-${status}`}
                    className="gap-2"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
                    Yeniden adlandır
                  </ContextMenuItem>
                  <ContextMenuSub>
                    <ContextMenuSubTrigger className="gap-2">
                      <Palette className="h-3.5 w-3.5" strokeWidth={2} />
                      Renk seç
                    </ContextMenuSubTrigger>
                    <ContextMenuSubContent className="w-44">
                      <ContextMenuItem
                        onSelect={() =>
                          patchColumn(status, { color: "" }, "Renk kaldırıldı")
                        }
                        data-testid={`kanban-column-color-none-${status}`}
                        className="gap-2"
                      >
                        <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-zinc-300 bg-white" />
                        <span className="flex-1">Renksiz</span>
                        {!pref.color ? (
                          <span className="text-[10px] text-zinc-400">✓</span>
                        ) : null}
                      </ContextMenuItem>
                      {LIST_PALETTE.map((p) => (
                        <ContextMenuItem
                          key={p.key}
                          onSelect={() =>
                            patchColumn(status, { color: p.key }, `Renk: ${p.label}`)
                          }
                          data-testid={`kanban-column-color-${p.key}-${status}`}
                          className="gap-2"
                        >
                          <span
                            className="inline-block h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
                            style={{ backgroundColor: p.hex }}
                          />
                          <span className="flex-1">{p.label}</span>
                          {pref.color === p.key ? (
                            <span className="text-[10px] text-zinc-400">✓</span>
                          ) : null}
                        </ContextMenuItem>
                      ))}
                    </ContextMenuSubContent>
                  </ContextMenuSub>
                  <ContextMenuItem
                    onSelect={() => setEmojiTarget(status)}
                    data-testid={`kanban-column-emoji-btn-${status}`}
                    className="gap-2"
                  >
                    <Smile className="h-3.5 w-3.5" strokeWidth={2} />
                    Emoji seç
                  </ContextMenuItem>
                  {pref.emoji ? (
                    <ContextMenuItem
                      onSelect={() =>
                        patchColumn(status, { emoji: "" }, "Emoji kaldırıldı")
                      }
                      data-testid={`kanban-column-emoji-clear-${status}`}
                      className="gap-2 text-zinc-500"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={2} />
                      Emojiyi kaldır
                    </ContextMenuItem>
                  ) : null}
                </ContextMenuContent>
              </ContextMenu>
              {tasks
                .filter((tk) => tk.status === status)
                .map((tk) => (
                  <div
                    key={tk.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("task-id", tk.id)}
                    onClick={() => onOpen(tk)}
                    data-testid={`kanban-card-${tk.id}`}
                    className="cursor-pointer rounded-md border border-zinc-200 bg-white p-3 hover:border-zinc-400 transition-colors"
                  >
                    <div className="text-sm font-medium text-zinc-900">{tk.title}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1">
                        <InlineAssigneePicker
                          assigneeId={tk.assignee_id}
                          users={users}
                          testId={`kanban-inline-assignee-${tk.id}`}
                          canReassign={canEditProtectedTaskFields(currentUser, tk)}
                          currentUserId={currentUser?.id}
                          onSelect={(uid) => onUpdate(tk.id, { assignee_id: uid })}
                        />
                        <CoAssigneeAvatars task={tk} users={users} />
                      </span>
                      <InlinePriorityPicker
                        priority={tk.priority}
                        testId={`kanban-inline-priority-${tk.id}`}
                        onSelect={(p) => onUpdate(tk.id, { priority: p })}
                      />
                    </div>
                  </div>
                ))}
            </div>
          );
        })}
      </div>

      {/* Rename column dialog */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => {
          if (!o && !savingRename) setRenameTarget(null);
        }}
      >
        <DialogContent
          data-testid="kanban-rename-dialog"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Kolonu yeniden adlandır</DialogTitle>
            <DialogDescription>
              Boş bırakırsan varsayılan başlık kullanılır. Görevler etkilenmez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label
              htmlFor="kanban-rename-input"
              className="text-xs uppercase tracking-wider text-zinc-500"
            >
              Kolon başlığı
            </Label>
            <Input
              id="kanban-rename-input"
              data-testid="kanban-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitRename();
                }
              }}
              placeholder="ör. Backlog, İnceleme, Hazır"
              maxLength={40}
              autoFocus
              disabled={savingRename}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRenameTarget(null)}
              disabled={savingRename}
              data-testid="kanban-rename-cancel"
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              onClick={submitRename}
              disabled={savingRename}
              data-testid="kanban-rename-save"
              className="bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {savingRename ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emoji picker dialog */}
      <Dialog
        open={!!emojiTarget}
        onOpenChange={(o) => {
          if (!o) setEmojiTarget(null);
        }}
      >
        <DialogContent
          data-testid="kanban-emoji-dialog"
          className="max-w-fit p-3"
        >
          <DialogHeader className="mb-1">
            <DialogTitle className="text-sm">Kolon için emoji seç</DialogTitle>
          </DialogHeader>
          <Suspense
            fallback={
              <div className="flex h-[400px] w-[350px] items-center justify-center text-xs text-zinc-500">
                Emoji seçici yükleniyor…
              </div>
            }
          >
            <EmojiPicker
              width={350}
              height={420}
              onEmojiClick={(data) => {
                const target = emojiTarget;
                if (!target) return;
                setEmojiTarget(null);
                patchColumn(
                  target,
                  { emoji: data?.emoji || "" },
                  `Emoji ayarlandı: ${data?.emoji || ""}`,
                );
              }}
              searchPlaceHolder="Emoji ara..."
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
}
